/* eslint-disable @typescript-eslint/ban-ts-comment */
// @ts-nocheck
import React from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Loader2, FileText, Database, HardDrive, TrendingUp, Users, Calendar, Activity, Building2 } from 'lucide-react'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"

export default function StudiesAnalytics({ isStudiesFetching, studiesData, timeRange, setTimeRange }) {
    // Use studies data if available, otherwise fall back to placeholder data
    const studiesAnalytics = studiesData || {
        system: {
            totalHospitals: 0,
            totalStudies: 0,
            totalInstances: 0,
            totalStorageGB: 0,
        },
        hospitalMetrics: [],
        dailyUploads: [],
        modalityDistribution: {},
        uploadFrequency: {
            averagePerDay: 0,
            peakDay: 0,
            totalUploads: 0,
            recentUploads: 0,
        },
    }

    const overviewMetrics = [
        {
            title: "Total Hospitals",
            value: studiesAnalytics.system?.totalHospitals || 0,
            icon: Building2,
            description: "Active hospitals",
            trend: "System-wide"
        },
        {
            title: "Total Studies",
            value: studiesAnalytics.system?.totalStudies || 0,
            icon: FileText,
            description: "Total studies in system",
            trend: `${studiesAnalytics.uploadFrequency?.recentUploads || 0} recent`
        },
        {
            title: "Total Instances",
            value: studiesAnalytics.system?.totalInstances || 0,
            icon: Database,
            description: "Medical instances",
            trend: `${studiesAnalytics.system?.totalStudies > 0 ? (studiesAnalytics.system?.totalInstances / studiesAnalytics.system?.totalStudies).toFixed(1) : 0} avg/study`
        },
        {
            title: "Storage Used",
            value: `${studiesAnalytics.system?.totalStorageGB?.toFixed(2) || 0} GB`,
            icon: HardDrive,
            description: "Total storage used",
            trend: "System storage"
        }
    ]

    const uploadMetrics = [
        {
            title: "Average Per Day",
            value: `${studiesAnalytics.uploadFrequency?.averagePerDay?.toFixed(1) || 0}`,
            icon: TrendingUp,
            description: "Average uploads per day",
            trend: "Daily average"
        },
        {
            title: "Peak Day",
            value: studiesAnalytics.uploadFrequency?.peakDay || 0,
            icon: Activity,
            description: "Highest uploads in a day",
            trend: "Peak performance"
        },
        {
            title: "Total Uploads",
            value: studiesAnalytics.uploadFrequency?.totalUploads || 0,
            icon: FileText,
            description: "Total studies uploaded",
            trend: "All time"
        },
        {
            title: "Recent Uploads",
            value: studiesAnalytics.uploadFrequency?.recentUploads || 0,
            icon: Calendar,
            description: "Recent uploads",
            trend: "Latest activity"
        }
    ]

    const timeRangeOptions = [
        { value: '7', label: 'Last 7 days' },
        { value: '30', label: 'Last 30 days' },
        { value: '90', label: 'Last 90 days' },
        { value: '365', label: 'Last year' },
    ]

    return (
        <div className="h-full overflow-y-auto p-6 space-y-6">
            <div className="flex items-center justify-between">
                <div>
                    <h3 className="text-2xl font-bold">Studies Analytics</h3>
                    <p className="text-muted-foreground">System-wide studies and upload analytics</p>
                </div>
                <div className="flex items-center space-x-4">
                    <Select value={timeRange} onValueChange={setTimeRange}>
                        <SelectTrigger className="w-[180px]">
                            <SelectValue placeholder="Select time range" />
                        </SelectTrigger>
                        <SelectContent>
                            {timeRangeOptions.map((option) => (
                                <SelectItem key={option.value} value={option.value}>
                                    {option.label}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
            </div>

            {isStudiesFetching ? (
                <div className="flex items-center justify-center h-64">
                    <Loader2 className="h-8 w-8 animate-spin" />
                </div>
            ) : (
                <>
                    {/* System Overview Metrics */}
                    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
                        {overviewMetrics.map((metric, index) => (
                            <Card key={index}>
                                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                                    <CardTitle className="text-sm font-medium">
                                        {metric.title}
                                    </CardTitle>
                                    <metric.icon className="h-4 w-4 text-muted-foreground" />
                                </CardHeader>
                                <CardContent>
                                    <div className="text-2xl font-bold">{metric.value}</div>
                                    <p className="text-xs text-muted-foreground">
                                        {metric.description}
                                    </p>
                                    <p className="text-xs text-green-600 mt-1">
                                        {metric.trend}
                                    </p>
                                </CardContent>
                            </Card>
                        ))}
                    </div>

                    {/* Upload Frequency Metrics */}
                    <Card>
                        <CardHeader>
                            <CardTitle>Upload Frequency</CardTitle>
                            <CardDescription>
                                System upload performance metrics
                            </CardDescription>
                        </CardHeader>
                        <CardContent>
                            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
                                {uploadMetrics.map((metric, index) => (
                                    <div key={index} className="flex items-center space-x-4">
                                        <metric.icon className="h-8 w-8 text-blue-500" />
                                        <div className="flex-1">
                                            <p className="text-sm font-medium">{metric.title}</p>
                                            <p className="text-2xl font-bold">{metric.value}</p>
                                            <p className="text-xs text-muted-foreground">{metric.description}</p>
                                            <p className="text-xs text-blue-600">{metric.trend}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </CardContent>
                    </Card>

                    {/* Hospital Metrics */}
                    <Card>
                        <CardHeader>
                            <CardTitle>Hospital Performance</CardTitle>
                            <CardDescription>
                                Individual hospital metrics and utilization
                            </CardDescription>
                        </CardHeader>
                        <CardContent>
                            <div className="space-y-4 max-h-96 overflow-y-auto">
                                {studiesAnalytics.hospitalMetrics?.length > 0 ? (
                                    studiesAnalytics.hospitalMetrics.map((hospital, index) => (
                                        <div key={index} className="flex items-center justify-between p-3 border rounded-lg">
                                            <div className="flex-1">
                                                <div className="flex items-center space-x-2">
                                                    <Building2 className="h-4 w-4 text-muted-foreground" />
                                                    <div>
                                                        <p className="text-sm font-medium">{hospital.hospitalName}</p>
                                                        <p className="text-xs text-muted-foreground">{hospital.hospitalType}</p>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="flex items-center space-x-4">
                                                <div className="text-center">
                                                    <p className="text-sm font-medium">{hospital.totalStudies}</p>
                                                    <p className="text-xs text-muted-foreground">Studies</p>
                                                </div>
                                                <div className="text-center">
                                                    <p className="text-sm font-medium">{hospital.totalInstances}</p>
                                                    <p className="text-xs text-muted-foreground">Instances</p>
                                                </div>
                                                <div className="text-center">
                                                    <p className="text-sm font-medium">{hospital.totalStorageGB?.toFixed(2)} GB</p>
                                                    <p className="text-xs text-muted-foreground">Storage</p>
                                                </div>
                                                <div className="text-center">
                                                    <Badge variant={hospital.utilizationRate > 80 ? "destructive" : hospital.utilizationRate > 50 ? "default" : "secondary"}>
                                                        {hospital.utilizationRate}%
                                                    </Badge>
                                                    <p className="text-xs text-muted-foreground">Utilization</p>
                                                </div>
                                            </div>
                                        </div>
                                    ))
                                ) : (
                                    <div className="text-center text-muted-foreground py-8">
                                        No hospital data available
                                    </div>
                                )}
                            </div>
                        </CardContent>
                    </Card>

                    {/* Modality Distribution */}
                    <Card>
                        <CardHeader>
                            <CardTitle>Modality Distribution</CardTitle>
                            <CardDescription>
                                Studies breakdown by imaging modality
                            </CardDescription>
                        </CardHeader>
                        <CardContent>
                            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                                {Object.entries(studiesAnalytics.modalityDistribution || {}).map(([modality, count], index) => (
                                    <div key={index} className="flex items-center justify-between p-3 border rounded-lg">
                                        <div>
                                            <p className="text-sm font-medium">{modality}</p>
                                            <p className="text-xs text-muted-foreground">Modality</p>
                                        </div>
                                        <Badge variant="secondary">{count}</Badge>
                                    </div>
                                ))}
                                {Object.keys(studiesAnalytics.modalityDistribution || {}).length === 0 && (
                                    <div className="col-span-full text-center text-muted-foreground py-8">
                                        No modality data available
                                    </div>
                                )}
                            </div>
                        </CardContent>
                    </Card>

                    {/* Daily Uploads Chart */}
                    <Card>
                        <CardHeader>
                            <CardTitle>Daily Upload Trends</CardTitle>
                            <CardDescription>
                                Study uploads over the selected time period
                            </CardDescription>
                        </CardHeader>
                        <CardContent>
                            <div className="space-y-4">
                                {studiesAnalytics.dailyUploads?.length > 0 ? (
                                    <div className="grid gap-2">
                                        {studiesAnalytics.dailyUploads.slice(-10).map((day, index) => (
                                            <div key={index} className="flex items-center justify-between p-2 border rounded">
                                                <div className="flex items-center space-x-4">
                                                    <Calendar className="h-4 w-4 text-muted-foreground" />
                                                    <span className="text-sm font-medium">{day.date}</span>
                                                </div>
                                                <div className="flex items-center space-x-4">
                                                    <div className="text-sm">
                                                        <span className="font-medium">{day.studies}</span> studies
                                                    </div>
                                                    <div className="text-sm text-muted-foreground">
                                                        <span className="font-medium">{day.instances}</span> instances
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <div className="text-center text-muted-foreground py-8">
                                        No upload data available for the selected time period
                                    </div>
                                )}
                            </div>
                        </CardContent>
                    </Card>
                </>
            )}
        </div>
    )
} 